import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Modal, Alert, Button, Typography, message } from 'antd';
import { FileExclamationOutlined, DownloadOutlined } from '@ant-design/icons';
import { Box, Flex } from 'atoms';
import FilesUploadS3 from 'components/common/FilesUploadS3';
import {
	bulkUploadInvoicePaymentApi,
	getInvoicePaymentSampleDownload,
} from 'store/vendorBillSlice';

const { Text } = Typography;

const CSV_FORMATS = ['text/csv'];
const MAX_SIZE = 8192000; // 8 MB
const ENTITY = 'invoice_payment_bulk_upload_v2';

/**
 * BulkPaymentUploadModal
 *
 * Sheet-upload path for marking vendor-invoice payments (V2). Complements the
 * selection-based "Mark as Paid": the user uploads a CSV with one row per bill
 * (Vendor Bill Number, UTR Number, Paid Date (DD-MM-YYYY), Paid Amount). Rows are
 * processed per-row on the backend — valid rows are paid, invalid rows come back in
 * a downloadable error file.
 */
export default function BulkPaymentUploadModal({ visible, onClose, onCompleted }) {
	const dispatch = useDispatch();
	const [uploadedKey, setUploadedKey] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [result, setResult] = useState(null);

	const resetState = () => {
		setUploadedKey(null);
		setSubmitting(false);
		setResult(null);
	};

	const handleCancel = () => {
		resetState();
		onClose();
	};

	const handleFileUploaded = (files) => {
		// FilesUploadS3 returns the list of uploaded S3 file keys.
		setUploadedKey(Array.isArray(files) ? files[0] : files);
	};

	const handleSubmit = () => {
		if (!uploadedKey) {
			message.error('Please upload a CSV file first');
			return;
		}
		setSubmitting(true);
		dispatch(bulkUploadInvoicePaymentApi({ data_key: uploadedKey }))
			.then((res) => {
				const data = res?.data || {};
				setResult(data);
				// Only refresh the list when at least one payment was actually recorded.
				const success = data.success_count ?? data.success_row ?? 0;
				if (onCompleted && success > 0) onCompleted();
			})
			.catch(() => {
				message.error('Failed to process the uploaded sheet');
			})
			.finally(() => setSubmitting(false));
	};

	const successCount = result?.success_count ?? result?.success_row ?? 0;
	const errorCount = result?.error_count ?? result?.error_row ?? 0;
	const errorFileUrl = result?.url || result?.download_url;

	return (
		<Modal
			title="Upload Payment Sheet"
			open={visible}
			onCancel={handleCancel}
			destroyOnClose
			width={600}
			centered
			footer={[
				<Button key="cancel" onClick={handleCancel} disabled={submitting}>
					Close
				</Button>,
				<Button
					key="submit"
					type="primary"
					onClick={handleSubmit}
					disabled={!uploadedKey || submitting}
					loading={submitting}
				>
					Submit
				</Button>,
			]}
		>
			<Alert
				type="info"
				showIcon
				style={{ marginBottom: 16 }}
				message={
					<Text>
						Upload a CSV with columns <b>Vendor Bill Number</b>, <b>UTR Number</b>,{' '}
						<b>Paid Date (DD-MM-YYYY)</b>, <b>Paid Amount</b>. Each row pays one bill;
						invalid rows are returned in an error file.
					</Text>
				}
			/>

			<Flex justifyContent="flex-end" mb={2}>
				<Button
					type="link"
					icon={<DownloadOutlined />}
					onClick={() => getInvoicePaymentSampleDownload()}
					style={{ padding: 0 }}
				>
					Download Sample File
				</Button>
			</Flex>

			<Box border="1px solid #ccc" padding="20px" borderRadius="4px">
				<FilesUploadS3
					entity={ENTITY}
					successCallBack={handleFileUploaded}
					name="Payment Sheet"
					size={MAX_SIZE}
					fileformates={CSV_FORMATS}
					filetypetoShow="CSV"
				/>
			</Box>

			{result && (
				<Box mt={3}>
					<Flex>
						<Box mr={3} color="green" fontSize="16px">
							Success Count: {successCount}
						</Box>
						<Box color="red" fontSize="16px">
							Error Count: {errorCount}
						</Box>
					</Flex>
					{errorFileUrl && (
						<Box mt={2}>
							<a
								href={errorFileUrl}
								download={errorFileUrl}
								target="_blank"
								rel="noreferrer"
							>
								<Button icon={<FileExclamationOutlined />}>
									Download Error File
								</Button>
							</a>
						</Box>
					)}
				</Box>
			)}
		</Modal>
	);
}

BulkPaymentUploadModal.propTypes = {
	visible: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	onCompleted: PropTypes.func,
};

BulkPaymentUploadModal.defaultProps = {
	visible: false,
	onCompleted: null,
};
